import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, MapPin, Ear } from "lucide-react";
import { KineticLine, FadeUp, Overline } from "../components/motion";
import { API } from "../constants/site";

const inputCls =
  "w-full rounded-sm border border-stone-300 bg-white px-4 py-3 text-sm text-ink placeholder:text-stone-400 outline-none transition-colors duration-300 focus:border-terracotta focus:ring-2 focus:ring-terracotta/20";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Your message has been sent. Winnie reads every note from the community.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-32 lg:pt-40">
      <section className="mx-auto grid max-w-7xl gap-16 px-6 pb-28 lg:grid-cols-12 lg:px-10">
        <div className="lg:col-span-5">
          <Overline>Contact</Overline>
          <h1 className="mt-6 font-display text-5xl font-bold leading-[0.95] tracking-tighter text-ink sm:text-6xl">
            <KineticLine delay={0.1}>Ready to</KineticLine>
            <KineticLine delay={0.22}>
              <span className="text-terracotta">listen.</span>
            </KineticLine>
          </h1>
          <FadeUp delay={0.3}>
            <p className="mt-8 text-base leading-relaxed text-stone2 md:text-lg">
              A good trustee starts by listening. Share your questions, concerns, or hopes for our
              schools — every message is read and valued.
            </p>
            <div className="mt-12 space-y-6">
              <div data-testid="contact-info-listening" className="flex items-start gap-4">
                <Ear size={22} className="mt-1 text-terracotta" strokeWidth={1.5} />
                <div>
                  <p className="font-display font-bold text-ink">Community first</p>
                  <p className="mt-1 text-sm text-stone2">Your perspective shapes this campaign's priorities.</p>
                </div>
              </div>
              <div data-testid="contact-info-region" className="flex items-start gap-4">
                <MapPin size={22} className="mt-1 text-terracotta" strokeWidth={1.5} />
                <div>
                  <p className="font-display font-bold text-ink">Vaughan, York Region</p>
                  <p className="mt-1 text-sm text-stone2">Serving students and families across our community.</p>
                </div>
              </div>
              <div data-testid="contact-info-response" className="flex items-start gap-4">
                <Mail size={22} className="mt-1 text-terracotta" strokeWidth={1.5} />
                <div>
                  <p className="font-display font-bold text-ink">Every note matters</p>
                  <p className="mt-1 text-sm text-stone2">The campaign team follows up on every message received.</p>
                </div>
              </div>
            </div>
          </FadeUp>
        </div>

        <FadeUp delay={0.15} className="lg:col-span-7">
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="border border-sand bg-white p-8 lg:p-12"
          >
            <h2 className="font-display text-2xl font-bold tracking-tight text-ink md:text-3xl">
              Send Winnie a Message
            </h2>
            <p className="mt-2 text-sm text-stone2">Questions, ideas, or concerns about our schools — all welcome.</p>

            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="c-name" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-stone2">Full Name *</label>
                <input id="c-name" data-testid="contact-name-input" required className={inputCls} placeholder="Your name"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label htmlFor="c-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-stone2">Email *</label>
                <input id="c-email" data-testid="contact-email-input" required type="email" className={inputCls} placeholder="you@example.com"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="c-message" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-stone2">Message *</label>
              <textarea id="c-message" data-testid="contact-message-input" required rows={6} className={inputCls}
                placeholder="What's on your mind?"
                value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>

            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={loading}
              className="group mt-9 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-terracotta px-7 py-4 font-semibold text-cream transition-all duration-300 hover:bg-terracotta-dark active:scale-[0.98] disabled:opacity-60 sm:w-auto"
            >
              {loading ? "Sending..." : "Send Message"}
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </form>
        </FadeUp>
      </section>
    </main>
  );
}
